import {
  api,
  ApiError,
  type PlayerAccountReactivateResponse,
  type PlayerArchiveResponse,
  type PlayerPinResponse,
  type PlayerProfile,
} from "./api";
import { askChoice, askConfirm, showNotice } from "./confirmDialog";

/** Cuentas familiares en el panel del admin (Eduardo 2026-09-29).
 *
 * Una cuenta familiar es el usuario del tutor con varios hijos. «Generar PIN»
 * sobre un hijo sin acceso propio ya no cambia la contraseña del tutor: el
 * backend responde 409 `family_account_pin` con la explicación, y la pantalla
 * la muestra en un diálogo con lo que SÍ se puede hacer: «Acceso propio» para
 * el hijo, cambiar la contraseña de la cuenta familiar (solo si el tutor la
 * pidió, confirmándolo otra vez) o reactivar la cuenta si el error viejo de
 * archivar a un hermano la dejó desactivada.
 *
 * Un PIN nunca reactiva una cuenta desactivada (tampoco la de un jugador de
 * un solo perfil): el backend responde 409 `account_disabled_pin` con la misma
 * forma de detalles, y el mismo diálogo ofrece «Reactivar cuenta» cuando le
 * sirve al admin. */

export const FAMILY_ACCOUNT_PIN_CODE = "family_account_pin";
export const ACCOUNT_DISABLED_PIN_CODE = "account_disabled_pin";

export interface FamilyAccountPinConflict {
  playerProfileId: number | null;
  message: string;
  /** false = cuenta desactivada que no es familiar (`account_disabled_pin`). */
  isFamilyAccount: boolean;
  guardianName: string | null;
  guardianEmail: string | null;
  guardianStatus: string | null;
  /** Solo los jugadores de la familia que este admin administra. */
  familyProfiles: string[];
  /** Los que no administra: se cuentan, no se nombran. */
  familyProfilesHidden: number;
  /** El perfil es el del titular de la cuenta (el papá que juega), no un hijo. */
  isAccountHolder: boolean;
  canGrantOwnAccess: boolean;
  canResetFamilyPassword: boolean;
  canReactivateAccount: boolean;
}

function textOrNull(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

/** El 409 de «Generar PIN» sobre un perfil de cuenta familiar (o sobre una
 * cuenta desactivada), o null si el error es otro. */
export function familyAccountPinConflict(err: unknown): FamilyAccountPinConflict | null {
  if (!(err instanceof ApiError)) return null;
  if (err.code !== FAMILY_ACCOUNT_PIN_CODE && err.code !== ACCOUNT_DISABLED_PIN_CODE) return null;
  const isFamilyAccount = err.code === FAMILY_ACCOUNT_PIN_CODE;
  const details = err.details ?? {};
  const guardian =
    details.guardian && typeof details.guardian === "object" ? (details.guardian as Record<string, unknown>) : {};
  const profiles = Array.isArray(details.family_profiles)
    ? details.family_profiles.filter((name): name is string => typeof name === "string" && name.trim() !== "")
    : [];
  const hidden = typeof details.family_profiles_hidden === "number" ? details.family_profiles_hidden : 0;
  return {
    playerProfileId: typeof details.player_profile_id === "number" ? details.player_profile_id : null,
    message:
      err.message ||
      (isFamilyAccount
        ? "Este jugador entra con una cuenta familiar: un PIN aquí cambiaría la contraseña de toda la familia."
        : "La cuenta de este jugador está desactivada y un PIN no la reactiva."),
    isFamilyAccount,
    guardianName: textOrNull(guardian.full_name),
    guardianEmail: textOrNull(guardian.email),
    guardianStatus: textOrNull(guardian.status),
    familyProfiles: profiles,
    familyProfilesHidden: hidden,
    isAccountHolder: details.is_account_holder === true,
    canGrantOwnAccess: details.can_grant_own_access === true,
    canResetFamilyPassword: details.can_reset_family_password === true,
    canReactivateAccount: details.can_reactivate_account === true,
  };
}

/** «Sam», «Sam y Leo», «Sam, Leo y 2 jugadores más», «otro jugador». */
export function familyProfilesPhrase(names: string[], hidden: number): string {
  const shown = names.slice(0, 3);
  const extra = names.length - shown.length + Math.max(0, hidden);
  if (shown.length === 0) return extra === 1 ? "otro jugador" : `otros ${extra} jugadores`;
  if (extra > 0) shown.push(extra === 1 ? "1 jugador más" : `${extra} jugadores más`);
  if (shown.length === 1) return shown[0];
  return `${shown.slice(0, -1).join(", ")} y ${shown[shown.length - 1]}`;
}

export type FamilyAccountPinAction = "own_access" | "reset_family" | "reactivate" | null;

const RESET_FAMILY_LABEL = "El tutor pidió una contraseña nueva";

/** Segunda confirmación antes de cambiar la contraseña de TODA la familia. */
export function confirmFamilyPasswordReset(
  conflict: FamilyAccountPinConflict,
  playerName: string,
  signal?: AbortSignal,
): Promise<boolean> {
  const everyone = familyProfilesPhrase([playerName, ...conflict.familyProfiles], conflict.familyProfilesHidden);
  const owner = conflict.guardianName ? ` de ${conflict.guardianName}` : "";
  return askConfirm(
    `Vas a cambiar la contraseña de la cuenta familiar${owner}. ${everyone} entrarán con el PIN nuevo ` +
      "y la primera vez tendrán que crear otra contraseña. Hazlo solo si el tutor te lo pidió.",
    { acceptLabel: "Sí, cambiar la contraseña familiar", tone: "danger", dismissLabel: "No, regresar", signal },
  );
}

/** Explica el 409 en un diálogo (responde al clic en «Generar PIN») y dice
 * qué eligió el admin. Nunca cambia nada con un solo clic: cambiar la
 * contraseña familiar siempre pasa por una confirmación. `signal` retira las
 * preguntas si la pantalla del jugador se deja (ver useAbortableConfirm). */
export async function explainFamilyAccountPin(
  conflict: FamilyAccountPinConflict,
  playerName: string,
  signal?: AbortSignal,
): Promise<FamilyAccountPinAction> {
  if (conflict.guardianStatus === "disabled") {
    if (!conflict.canReactivateAccount) {
      await showNotice(conflict.message);
      return null;
    }
    const reactivate = await askConfirm(conflict.message, {
      acceptLabel: conflict.isFamilyAccount ? "Reactivar la cuenta familiar" : "Reactivar cuenta",
      dismissLabel: "Entendido",
      signal,
    });
    return reactivate ? "reactivate" : null;
  }
  if (conflict.isAccountHolder) {
    if (!conflict.canResetFamilyPassword) {
      await showNotice(conflict.message);
      return null;
    }
    // El mensaje ya nombra a todos los que comparten la cuenta: este es el
    // segundo clic, la confirmación.
    const reset = await askConfirm(conflict.message, {
      acceptLabel: "Sí, cambiar la contraseña de la cuenta",
      tone: "danger",
      dismissLabel: "No, regresar",
      signal,
    });
    return reset ? "reset_family" : null;
  }
  if (conflict.canGrantOwnAccess && conflict.canResetFamilyPassword) {
    const choice = await askChoice(conflict.message, {
      acceptLabel: "Ir a «Acceso propio»",
      secondaryLabel: RESET_FAMILY_LABEL,
      dismissLabel: "Entendido",
      signal,
    });
    if (choice === "accept") return "own_access";
    if (choice === "secondary") return (await confirmFamilyPasswordReset(conflict, playerName, signal)) ? "reset_family" : null;
    return null;
  }
  if (conflict.canGrantOwnAccess) {
    const go = await askConfirm(conflict.message, { acceptLabel: "Ir a «Acceso propio»", dismissLabel: "Entendido", signal });
    return go ? "own_access" : null;
  }
  if (conflict.canResetFamilyPassword) {
    const wants = await askConfirm(conflict.message, { acceptLabel: RESET_FAMILY_LABEL, dismissLabel: "Entendido", signal });
    if (!wants) return null;
    return (await confirmFamilyPasswordReset(conflict, playerName, signal)) ? "reset_family" : null;
  }
  await showNotice(conflict.message);
  return null;
}

export type FamilyAccountPinOutcome = { kind: "own_access" } | { kind: "message"; text: string } | { kind: "none" };

/** Lo que sigue tras el 409: explica, y si el admin eligió algo lo hace
 * (PIN confirmado de la cuenta familiar o reactivarla). Los errores de esas
 * llamadas los maneja la pantalla, como los de «Generar PIN». */
export async function resolveFamilyAccountPin(
  token: string,
  player: Pick<PlayerProfile, "id" | "display_name">,
  conflict: FamilyAccountPinConflict,
  signal?: AbortSignal,
): Promise<FamilyAccountPinOutcome> {
  const action = await explainFamilyAccountPin(conflict, player.display_name, signal);
  // Se dejó la ficha (o la lista) mientras se preguntaba: no se toca la cuenta.
  if (signal?.aborted) return { kind: "none" };
  if (action === "own_access") return { kind: "own_access" };
  if (action === "reset_family") {
    const response = await api.players.generatePin(token, player.id, { confirmFamilyAccount: true });
    return { kind: "message", text: pinSuccessMessage(response.data, player.display_name) };
  }
  if (action === "reactivate") {
    const response = await api.players.reactivateAccount(token, player.id);
    return { kind: "message", text: reactivateSuccessMessage(response.data, player.display_name) };
  }
  return { kind: "none" };
}

/** Texto de éxito de «Generar PIN» / «PIN = últimos 4 del celular». */
export function pinSuccessMessage(data: PlayerPinResponse["data"], playerName: string): string {
  const loginUser = data.username || data.email;
  if (data.target === "own_access") {
    return (
      `PIN temporal para el acceso propio de ${playerName}. Usuario: ${loginUser} · PIN: ${data.pin}. ` +
      "La cuenta del tutor no cambia. Al entrar deberá crear su contraseña."
    );
  }
  if (data.target === "family_account") {
    return (
      `PIN temporal de la cuenta familiar con la que entra ${playerName}. Usuario: ${loginUser} · PIN: ${data.pin}. ` +
      "Sirve para toda la familia; al entrar, el tutor deberá crear una contraseña nueva."
    );
  }
  return `PIN temporal generado. Usuario: ${loginUser} · PIN: ${data.pin}. Al entrar deberá crear su contraseña.`;
}

/** Texto de éxito de «Reactivar cuenta». */
export function reactivateSuccessMessage(data: PlayerAccountReactivateResponse["data"], playerName: string): string {
  if (!data.reactivated) return `La cuenta con la que entra ${playerName} ya estaba activa.`;
  const who = familyProfilesPhrase([playerName, ...data.family_profiles], data.family_profiles_hidden);
  const verb = data.family_profiles.length + data.family_profiles_hidden > 0 ? "ya pueden" : "ya puede";
  return `Cuenta reactivada: ${who} ${verb} entrar otra vez con su contraseña de siempre (o con «Olvidé mi contraseña»).`;
}

/** Estado de la cuenta de acceso en la ficha del jugador. Antes decía
 * «Activa (disabled)» para una cuenta desactivada. */
export function accountStatusLabel(status: string | null | undefined): string {
  if (status === "active") return "Activa";
  if (status === "disabled") return "Desactivada";
  return status ? `Estado: ${status}` : "—";
}

/** Texto de éxito de «Eliminar» (archivar) un jugador. */
export function archiveSuccessMessage(data: PlayerArchiveResponse["data"], playerName: string): string {
  const base = `Jugador eliminado: ${playerName}.`;
  if (data.account_kept_reason === "family_account") {
    return `${base} La cuenta familiar con la que entraba sigue activa para los demás jugadores de la familia.`;
  }
  if (data.account_kept_reason === "family_account_disabled") {
    return (
      `${base} La cuenta familiar con la que entraba ya estaba desactivada desde antes: para que el resto ` +
      "de la familia vuelva a entrar, abre la ficha de otro jugador de la familia y pulsa «Reactivar cuenta»."
    );
  }
  if (data.account_kept_reason === "staff_account") {
    return `${base} Su cuenta de administrador sigue activa.`;
  }
  if (data.account_disabled) {
    return `${base} Su cuenta de acceso quedó desactivada.`;
  }
  return base;
}
